import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { categoryService } from '../services/firestore';
import { Category } from '../types';
import './HomePage.css';

const HomePage: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const categoriesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesData = await categoryService.getAll();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px'
      }
    );

    const currentCategories = categoriesRef.current;
    if (currentCategories) {
      observer.observe(currentCategories);
    }

    return () => {
      if (currentCategories) {
        observer.unobserve(currentCategories);
      }
    };
  }, []);

  

  if (loading) {
    return (
      <div className="homepage">
        <section className="categories">
          <div className="container">
            <h2 className="section-title">Shop by Category</h2>
            <div className="categories-grid">
              {[1, 2, 3, 4].map((index) => (
                <div key={index} className="skeleton-card">
                  <div className="skeleton-image"></div>
                  <div className="skeleton-content">
                    <div className="skeleton-title"></div>
                    <div className="skeleton-description"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="homepage">
      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            {/* Animated JK Prints Logo */}
            <div className="jk-logo">
              <div className="jk-letters">
                <span className="letter-j">J</span>
                <span className="letter-k">K</span>
              </div>
              <div className="prints-text">Prints</div>
            </div>
            
            {/* Main Hero Content */}
            <h1 className="hero-title">Custom Printing Made Easy</h1>
            <p className="hero-subtitle">
              Professional printing solutions for cups, t-shirts, bags, clothes and more. 
              Quality you can trust, prices you'll love.
            </p>
            <div className="hero-buttons">
              <Link to="/products" className="btn btn-primary btn-large">
                Shop Now
              </Link>
              <Link to="/custom-order" className="btn btn-outline btn-large">
                Custom Order
              </Link>
            </div>
          </div>
        </div>
      </section>


      {/* Categories Section */}
      {categories.length > 0 && (
        <section className="categories" ref={categoriesRef}>
          <div className="container">
            <h2 className="section-title">Shop by Category</h2>
            <div className="categories-grid">
              {categories.map((category, index) => {
                const animations = [
                  'slideInLeft 0.6s ease-out',
                  'slideInRight 0.6s ease-out',
                  'fadeInUp 0.6s ease-out',
                  'slideInLeft 0.6s ease-out'
                ];
                const animationDelay = index * 0.15;
                
                return (
                  <Link 
                    key={category.id} 
                    to={`/products?category=${category.id}`}
                    className="category-card"
                    style={{
                      animation: `${animations[index % animations.length]} ${animationDelay}s both`
                    }}
                  >
                  <div className="category-image">
                    <div className="category-badge">
                      <span className="badge-number">{index + 1}</span>
                    </div>
                    <img 
                      src={category.imageUrl || ''} 
                      alt={category.name}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                      }}
                    />
                  </div>
                  <div className="category-info">
                    <h3>{category.name}</h3>
                    <p>{category.description}</p>
                  </div>
                </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;
